import { RestClient } from "./restClient";

export interface DepartmentUserRef {
  user_id: string;
}

export interface Department {
  id: number;
  name: string;
  parent_id: number | null;
  member_count?: number;
}

export interface MyDepartments {
  department_ids: string[];
}

interface DepartmentListEnvelope {
  departments: Department[];
}

/** Parse a raw FGA object id of the form "department:{company_id}/{dept_id}"
 *  into its trailing numeric department id. Returns null when the shape is
 *  unexpected so callers can drop unparseable entries rather than pass NaN. */
/** Parse /mine ids: bare numeric ("5") or FGA "department:{company}/{id}". */
export function parseMineDeptId(raw: string): number | null {
  const n = Number(raw);
  if (Number.isInteger(n)) return n;
  return parseDeptObjectId(raw);
}

export function parseDeptObjectId(objectId: string): number | null {
  // Strip the "department:" type prefix, then take the segment after "/".
  const afterType = objectId.split(":").slice(1).join(":");
  const tail = afterType.split("/").pop();
  if (!tail) return null;
  const n = Number(tail);
  return Number.isInteger(n) ? n : null;
}

/** Wraps the /departments HTTP surface. Methods throw RestError on non-2xx. */
export class DepartmentsApi {
  constructor(private client: RestClient) {}

  /** All departments in the caller's company — for an admin to pick any.
   *  The endpoint wraps the list as {departments: [...]}; unwrap it so callers
   *  get a plain array (a bare `.length` on the envelope was always undefined,
   *  which kept department creation disabled). */
  async list(): Promise<Department[]> {
    const res = await this.client.get<DepartmentListEnvelope>(
      "/core/v1/departments",
    );
    return res.departments ?? [];
  }

  /** Raw FGA object ids of the departments the caller manages. Empty array
   *  means the user manages no departments. */
  listMine(): Promise<MyDepartments> {
    return this.client.get<MyDepartments>("/core/v1/departments/mine");
  }

  /** Create a department (admin only). 201 → the created row. */
  create(name: string, parentId?: number): Promise<Department> {
    const body: { name: string; parent_id?: number } = { name };
    if (parentId !== undefined) body.parent_id = parentId;
    return this.client.post<Department>("/core/v1/departments", body);
  }

  /** Add a user as a member of a department (admin only). 204 on success. */
  addMember(deptId: number, userId: string): Promise<void> {
    return this.client.post<void>(`/core/v1/departments/${deptId}/members`, {
      user_id: userId,
    });
  }

  /** List members of a department (admin only). */
  async listMembers(deptId: number): Promise<DepartmentUserRef[]> {
    const res = await this.client.get<{ members: DepartmentUserRef[] }>(
      `/core/v1/departments/${deptId}/members`,
    );
    return res.members ?? [];
  }

  /** Remove a user from a department (admin only). 204 on success. */
  removeMember(deptId: number, userId: string): Promise<void> {
    return this.client.delete<void>(
      `/core/v1/departments/${deptId}/members/${encodeURIComponent(userId)}`,
    );
  }

  /** Grant a user skill-editor on one department (admin only). 204. */
  addEditor(deptId: number, userId: string): Promise<void> {
    return this.client.post<void>(`/core/v1/departments/${deptId}/editors`, {
      user_id: userId,
    });
  }

  /** List skill editors for one department (admin only). */
  async listEditors(deptId: number): Promise<DepartmentUserRef[]> {
    const res = await this.client.get<{ editors: DepartmentUserRef[] }>(
      `/core/v1/departments/${deptId}/editors`,
    );
    return res.editors ?? [];
  }

  /** Revoke a user's skill-editor on one department (admin only). 204. */
  removeEditor(deptId: number, userId: string): Promise<void> {
    return this.client.delete<void>(
      `/core/v1/departments/${deptId}/editors/${encodeURIComponent(userId)}`,
    );
  }

  /** Grant a user skill-editor across ALL departments (company#skills_editor,
   *  admin only). 204. */
  addAllDeptsEditor(userId: string): Promise<void> {
    return this.client.post<void>("/core/v1/departments/editors/all", {
      user_id: userId,
    });
  }

  /** List all-departments skill editors (admin only). */
  async listAllDeptsEditors(): Promise<DepartmentUserRef[]> {
    const res = await this.client.get<{ editors: DepartmentUserRef[] }>(
      "/core/v1/departments/editors/all",
    );
    return res.editors ?? [];
  }

  /** Revoke a user's all-departments skill-editor grant (admin only). 204. */
  removeAllDeptsEditor(userId: string): Promise<void> {
    return this.client.delete<void>(
      `/core/v1/departments/editors/all/${encodeURIComponent(userId)}`,
    );
  }
}
